import React, { ChangeEvent, useEffect, useState } from 'react';
import {
	CircularProgress,
	FormControlLabel,
	FormGroup,
	Grid,
	Switch,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getCertStatus, setCertStatus } from '@Actions/implementation';
import { useSettings, Text } from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { Label, ToggleContainer } from './DataConnectorControl.styles';
import DisableDataConnectorDialog from '@Components/DisableDataConnetorDialog';
import { useGlobalization } from '@Translations/useGlobalization';
import { CertificateStatus } from '@Types/implementation';

const DataConnectorControl = () => {
	const { t } = useGlobalization();

	const dispatch = useDispatch();

	const { certificateStatus } = useSelector(getValue).implementation;

	const { companySettings } = useSettings();

	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	useEffect(() => {
		if (companySettings.organization) {
			dispatch(getCertStatus(companySettings.organization ?? ''));
		}
	}, [companySettings.organization]);

	function handleSwitchChanged(event: ChangeEvent<HTMLInputElement>): void {
		if (event.target.checked) {
			dispatch(
				setCertStatus({
					thingName: companySettings.organization ?? '',
					desiredStatus: 'Enabled',
				}),
			);
		} else {
			setIsDeleteDialogOpen(true);
		}
	}

	const handleClickOnDisableDataConnectorModelButtonConfirm = () => {
		setIsDeleteDialogOpen(false);
		dispatch(
			setCertStatus({
				thingName: companySettings.organization ?? '',
				desiredStatus: 'Disabled',
			}),
		);
	};

	const renderToggle = (certificateStatus: CertificateStatus | undefined) => {
		let contents = (
			<Text paragraph={false} variant="caption-medium-strong" noWrap>
				{t('Error')}
			</Text>
		);

		const certSatus = certificateStatus?.status;
		const resSatus = certificateStatus?.response?.status;
		const isLoading = certSatus === 'idle' || certSatus === 'pending';
		const showControl =
			certSatus === 'success' &&
			(resSatus === 'Enabled' || resSatus === 'Disabled');

		if (isLoading) {
			contents = (
				<>
					<CircularProgress size={18} />
					<Text paragraph={false} variant="caption-medium" noWrap>
						{t('Checking...')}
					</Text>
				</>
			);
		} else if (showControl) {
			const checked = resSatus === 'Enabled';
			const label = checked ? t('Enabled') : t('Disabled');
			contents = (
				<FormGroup>
					<FormControlLabel
						control={
							<Switch
								size="small"
								checked={checked}
								onChange={handleSwitchChanged}
							/>
						}
						label={
							<Text
								paragraph={false}
								variant="caption-medium"
								noWrap>
								{label}
							</Text>
						}
					/>
				</FormGroup>
			);
		}

		return (
			<ToggleContainer
				xs
				display="flex"
				direction="row"
				justifyContent="space-between"
				withMargin={isLoading}>
				{contents}
			</ToggleContainer>
		);
	};

	return (
		<Grid item>
			<Grid item>
				<Label>{t('Data Connector')}</Label>
			</Grid>

			<Grid item>{renderToggle(certificateStatus)}</Grid>

			<DisableDataConnectorDialog
				onCancel={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleClickOnDisableDataConnectorModelButtonConfirm}
				open={isDeleteDialogOpen}
			/>
		</Grid>
	);
};

export default DataConnectorControl;
